import { legendDisplayName } from "@openrift/shared/card-name";
import { getOrientation } from "@openrift/shared/card-orientation";
import { imageUrl } from "@openrift/shared/image-url";
import { preferredPrinting } from "@openrift/shared/printing-select";
import type { CardDetailResponse } from "@openrift/shared/types/api/catalog";
import type { Printing } from "@openrift/shared/types/catalog";

import { formatPrice, formatPriceEur } from "@/lib/format";

type MetaPrinting = Pick<
  Printing,
  "printingId" | "side" | "slug" | "language" | "canonicalRank" | "images" | "printedRulesText"
>;

const META_DESCRIPTION_LIMIT = 155;

/** Per-marketplace price aggregate precomputed by the card-detail loader for SSR meta tags. */
export interface CardMarketplaceOffer {
  seller: string;
  currency: string;
  priceLow: number;
  priceHigh: number;
  offerCount: number;
}

// Uses the first offer in the loader's marketplace order, since
// cross-currency minimums aren't comparable.
export function buildCardPriceLine(offers: readonly CardMarketplaceOffer[]): string | null {
  const offer = offers[0];
  if (!offer) {
    return null;
  }
  const formatted =
    offer.currency === "EUR" ? formatPriceEur(offer.priceLow) : formatPrice(offer.priceLow);
  return `Prices from ${formatted} (${offer.seller}).`;
}

// Mirrors the page component's own preferredPrinting call, so the SSR
// og:image/description matches what a fresh visitor lands on.
export function pickCardMetaPrinting<T extends MetaPrinting>(
  printings: readonly T[],
  languageOrder: readonly string[],
): T | undefined {
  if (printings.length === 0) {
    return undefined;
  }
  // preferredPrinting only reads `language` and `canonicalRank`, so this structural cast is safe.
  return (
    (preferredPrinting(printings as unknown as Printing[], languageOrder) as T | undefined) ??
    printings[0]
  );
}

// A `?printingId=` that exists on the card wins; otherwise (or if it
// belongs to a different card) falls back to the language-preferred printing.
/**
 * `printingRef` is the permalink slug from the path, or the uuid the older
 * `?printingId=` links still carry.
 */
/** `side` picks between the sides of a printing that shows the card on both. */
export function resolveCardMetaPrinting<T extends MetaPrinting>(
  printings: readonly T[],
  printingRef: string | undefined,
  languageOrder: readonly string[],
  side?: 1 | 2,
): T | undefined {
  const linked = printingRef
    ? printings.filter(
        (printing) => printing.slug === printingRef || printing.printingId === printingRef,
      )
    : [];
  return (
    linked.find((printing) => printing.side === (side ?? 1)) ??
    linked[0] ??
    pickCardMetaPrinting(printings, languageOrder)
  );
}

// Strips rules-text markup so emoji shortcodes (`:rb_energy_2:`), `[keyword:foo]`
// macros and markdown emphasis don't leak into unfurls.
export function buildCardMetaDescription(
  card: CardDetailResponse["card"],
  printing: Pick<MetaPrinting, "printedRulesText"> | undefined,
  labels?: { domains?: Record<string, string>; cardTypes?: Record<string, string> },
  offers?: readonly CardMarketplaceOffer[],
): string {
  const parts: string[] = [];

  const domainLabels =
    card.domains.length > 0
      ? card.domains.map((slug) => labels?.domains?.[slug] ?? slug).join("/")
      : null;
  const typeLabel = card.types.map((slug) => labels?.cardTypes?.[slug] ?? slug).join(" ");
  const typeLine = domainLabels ? `${domainLabels} ${typeLabel}` : typeLabel;
  parts.push(`${legendDisplayName(card)} is a ${typeLine} card from Riftbound.`);

  const priceLine = offers ? buildCardPriceLine(offers) : null;
  if (priceLine) {
    parts.push(priceLine);
  }

  const rulesText = printing?.printedRulesText;
  if (rulesText) {
    // Emphasis markers come off after the shortcodes: `:rb_energy_2:` carries
    // underscores of its own and would stop matching otherwise.
    const cleaned = rulesText
      .replaceAll(/\[.*?\]/gu, "")
      .replaceAll(/:[a-z0-9_]+:/giu, "")
      .replaceAll(/[*_]/gu, "")
      .replaceAll(/\s+/gu, " ")
      .trim();
    if (cleaned.length > 0) {
      const remaining = META_DESCRIPTION_LIMIT - parts.join(" ").length - 1;
      if (cleaned.length > remaining) {
        parts.push(`${cleaned.slice(0, remaining - 3)}...`);
      } else {
        parts.push(cleaned);
      }
    }
  }

  return parts.join(" ");
}

export function frontImageId(
  printing: { images: readonly { imageId: string }[] } | undefined,
): string | null {
  return printing?.images[0]?.imageId ?? null;
}

export interface PrintingArt {
  imageId: string | null;
  landscape: boolean;
}

/** A double-sided printing is named by both of its cards, side 1 first. */
export function printingDisplayName(
  printing: Pick<Printing, "card" | "side" | "otherSide">,
): string {
  const name = legendDisplayName(printing.card);
  if (!printing.otherSide) {
    return name;
  }
  return printing.side === 2
    ? `${printing.otherSide.cardName} // ${name}`
    : `${name} // ${printing.otherSide.cardName}`;
}

export function printingArt(printing: Printing | undefined): PrintingArt {
  return {
    imageId: frontImageId(printing),
    landscape: printing !== undefined && getOrientation(printing.card.types) === "landscape",
  };
}

export function getCardFrontImageFullUrl(
  printing: Pick<MetaPrinting, "images"> | undefined,
): string | undefined {
  const id = frontImageId(printing);
  return id ? imageUrl(id, "full") : undefined;
}
